import { useEffect, useState } from 'react';
import { useForm, useFieldArray, SubmitHandler } from 'react-hook-form';
import { X, Loader2, Plus, Trash2, Send } from 'lucide-react';
import { toast } from 'react-toastify';
import { CreateBranchPayload, CreateTenantPayload, Tenant, UpdateTenantPayload } from '../../../../types/tenant';
import { useCreateBranchMutation, useCreateTenantMutation, useUpdateTenantMutation } from '../../../../services/hooks/useTenants';
import { resendContactPersonInvitation } from '../../../../services/api/tenantService';
import { Button } from '../../../../components/common/Button';
import { ColorPicker } from '../../../../components/common/ColorPicker';
import { ImageUpload } from '../../../../components/common/ImageUpload';

interface TenantFormModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSaveSuccess: () => void;
    tenantToEdit?: Tenant | null;
    mode?: 'root' | 'branch';
    parentTenant?: Tenant | null;
}

interface ContactPersonInput {
    name: string;
    email: string;
    phone: string;
}

interface RoomInput {
    name: string;
    capacity: number;
}

interface FormInputs {
    name: string;
    cif: string;
    address: {
        street: string;
        city: string;
        postalCode: string;
        country: string;
    };
    contactPersons: ContactPersonInput[];
    rooms: RoomInput[];
    status: 'active' | 'inactive' | 'suspended';
    limits: {
        roomsQty: number;
        roomCapacityDefault: number | '';
        maxUsers: number | '';
    };
    metadata: {
        colors: {
            primary: string;
            secondary: string;
        };
        logo: string;
    };
}

const DEFAULT_VALUES: FormInputs = {
    name: '',
    cif: '',
    address: {
        street: '',
        city: '',
        postalCode: '',
        country: 'ES',
    },
    contactPersons: [{ name: '', email: '', phone: '' }],
    rooms: [{ name: '', capacity: 10 }],
    status: 'active',
    limits: {
        roomsQty: 1,
        roomCapacityDefault: '',
        maxUsers: 5,
    },
    metadata: {
        colors: {
            primary: '#000000',
            secondary: '#ffffff',
        },
        logo: '',
    },
};

const TenantFormModal = ({ isOpen, onClose, onSaveSuccess, tenantToEdit, mode = 'root', parentTenant = null }: TenantFormModalProps) => {
    const isEditing = !!tenantToEdit;
    const isBranchFlow = mode === 'branch' || tenantToEdit?.tenantType === 'branch';
    const createMutation = useCreateTenantMutation();
    const createBranchMutation = useCreateBranchMutation();
    const updateMutation = useUpdateTenantMutation();
    const isPending = createMutation.isPending || createBranchMutation.isPending || updateMutation.isPending;

    const {
        register,
        handleSubmit,
        reset,
        setValue,
        watch,
        control,
        formState: { errors },
    } = useForm<FormInputs>({ defaultValues: DEFAULT_VALUES });

    const { fields: contactFields, append: appendContact, remove: removeContact } = useFieldArray({
        control,
        name: 'contactPersons',
    });

    const { fields: roomFields, append: appendRoom, remove: removeRoom } = useFieldArray({
        control,
        name: 'rooms',
    });

    // B3 — fila cuyo reenvío está en vuelo, para el spinner y bloquear el resto.
    const [resendingIndex, setResendingIndex] = useState<number | null>(null);

    const logo = watch('metadata.logo');
    const primaryColor = watch('metadata.colors.primary');
    const secondaryColor = watch('metadata.colors.secondary');

    useEffect(() => {
        if (tenantToEdit) {
            reset({
                name: tenantToEdit.name,
                cif: tenantToEdit.cif ?? '',
                address: {
                    street: tenantToEdit.address?.street ?? '',
                    city: tenantToEdit.address?.city ?? '',
                    postalCode: tenantToEdit.address?.postalCode ?? '',
                    country: tenantToEdit.address?.country ?? 'ES',
                },
                contactPersons: tenantToEdit.contactPersons?.length
                    ? tenantToEdit.contactPersons.map((p) => ({ name: p.name, email: p.email, phone: p.phone ?? '' }))
                    : [{ name: '', email: '', phone: '' }],
                rooms: tenantToEdit.rooms?.length
                    ? tenantToEdit.rooms.map((r) => ({ name: r.name, capacity: r.capacity }))
                    : [{ name: '', capacity: 10 }],
                status: tenantToEdit.status,
                limits: {
                    roomsQty: tenantToEdit.limits.roomsQty,
                    roomCapacityDefault: tenantToEdit.limits.roomCapacityDefault ?? '',
                    maxUsers: tenantToEdit.limits.maxUsers ?? 5,
                },
                metadata: {
                    colors: {
                        primary: tenantToEdit.metadata?.colors?.primary ?? '#000000',
                        secondary: tenantToEdit.metadata?.colors?.secondary ?? '#ffffff',
                    },
                    logo: tenantToEdit.metadata?.logo ?? '',
                },
            });
            return;
        }
        reset(DEFAULT_VALUES);
    }, [tenantToEdit, reset]);

    // B3 — reenvío de invitación. Solo aplica a contactos ya guardados en el
    // centro: uno recién tecleado aún no tiene invitación que reenviar, y el
    // backend lo rechazaría por no pertenecer al tenant.
    const isSavedContact = (index: number) => {
        const email = watch(`contactPersons.${index}.email`)?.trim().toLowerCase();
        if (!email) return false;
        return (tenantToEdit?.contactPersons || []).some(
            (p) => p.email?.trim().toLowerCase() === email
        );
    };

    const handleResend = async (index: number) => {
        if (!tenantToEdit?._id) return;
        const email = watch(`contactPersons.${index}.email`)?.trim();
        if (!email) return;

        setResendingIndex(index);
        try {
            const res = await resendContactPersonInvitation(tenantToEdit._id, email);
            toast.success(res.message || `Invitación reenviada a ${email}`);
        } catch (error: any) {
            // El 409 ("ya tiene cuenta activa") trae la salida concreta en el
            // mensaje del servidor, así que se muestra tal cual.
            toast.error(error.response?.data?.message || 'No se pudo reenviar la invitación');
        } finally {
            setResendingIndex(null);
        }
    };

    const onSubmit: SubmitHandler<FormInputs> = (data) => {
        const basePayload = {
            ...data,
            address: {
                ...data.address,
                postalCode: data.address.postalCode || undefined,
            },
            contactPersons: data.contactPersons.map((p) => ({
                ...p,
                phone: p.phone || undefined,
            })),
            rooms: data.rooms.map((r) => ({
                name: r.name.trim(),
                capacity: Number(r.capacity),
            })),
            metadata: {
                ...data.metadata,
                logo: data.metadata.logo || undefined,
            },
            limits: {
                roomsQty: data.rooms.length,
                roomCapacityDefault: null,
                maxUsers: data.limits.maxUsers === '' ? undefined : Number(data.limits.maxUsers),
            },
        };

        if (isEditing) {
            const updatePayload: UpdateTenantPayload = {
                ...basePayload,
                status: data.status,
            };

            updateMutation.mutate(
                { id: tenantToEdit._id, data: updatePayload },
                { onSuccess: onSaveSuccess }
            );
            return;
        }

        if (isBranchFlow) {
            if (!parentTenant?._id) {
                return;
            }

            const branchPayload: CreateBranchPayload = {
                name: basePayload.name,
                address: basePayload.address,
                limits: basePayload.limits,
                metadata: basePayload.metadata,
                rooms: basePayload.rooms,
                status: basePayload.status,
            };

            createBranchMutation.mutate(
                { tenantId: parentTenant._id, data: branchPayload },
                { onSuccess: onSaveSuccess }
            );
            reset(DEFAULT_VALUES);
            return;
        }

        const rootPayload: CreateTenantPayload = {
            ...basePayload,
            cif: data.cif.trim().toUpperCase(),
        };

        createMutation.mutate(rootPayload, { onSuccess: onSaveSuccess });
        reset(DEFAULT_VALUES);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-[40rem] max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b">
                    <h2 className="text-lg font-semibold">
                        {isEditing ? (isBranchFlow ? 'Editar Sucursal' : 'Editar Tenant') : (isBranchFlow ? 'Nueva Sucursal' : 'Nuevo Tenant')}
                    </h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                        <X size={20} />
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit(onSubmit)} className="px-6 py-4 space-y-5">
                    {isBranchFlow && !isEditing && parentTenant && (
                        <div className="rounded border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-700">
                            Creando sucursal para: <strong>{parentTenant.name}</strong>
                        </div>
                    )}

                    {/* Datos de la empresa */}
                    <fieldset className="border rounded p-3">
                        <legend className="text-xs font-semibold text-gray-500 uppercase px-1">Datos de la empresa</legend>
                        <div className="grid grid-cols-2 gap-3 mt-2">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Nombre <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    {...register('name', { required: 'El nombre es requerido' })}
                                    className="w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                                    placeholder="Ej: Mi Empresa SL"
                                />
                                {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name.message}</p>}
                            </div>
                            {!isBranchFlow && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    CIF <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    {...register('cif', {
                                        required: 'El CIF es requerido',
                                        pattern: { value: /^[A-Z0-9]{9}$/i, message: 'CIF inválido (9 caracteres alfanuméricos)' },
                                    })}
                                    className="w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 uppercase"
                                    placeholder="Ej: B12345678"
                                    readOnly={isEditing}
                                />
                                {errors.cif && <p className="text-red-500 text-xs mt-1">{errors.cif.message}</p>}
                            </div>
                            )}
                        </div>
                    </fieldset>

                    {/* Dirección */}
                    <fieldset className="border rounded p-3">
                        <legend className="text-xs font-semibold text-gray-500 uppercase px-1">Dirección</legend>
                        <div className="grid grid-cols-2 gap-3 mt-2">
                            <div className="col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Calle <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    {...register('address.street', { required: 'La calle es requerida' })}
                                    className="w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                                    placeholder="Ej: Calle Mayor 1, 2º"
                                />
                                {errors.address?.street && <p className="text-red-500 text-xs mt-1">{errors.address.street.message}</p>}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Ciudad <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    {...register('address.city', { required: 'La ciudad es requerida' })}
                                    className="w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                                    placeholder="Ej: Madrid"
                                />
                                {errors.address?.city && <p className="text-red-500 text-xs mt-1">{errors.address.city.message}</p>}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Código Postal</label>
                                <input
                                    type="text"
                                    {...register('address.postalCode')}
                                    className="w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                                    placeholder="Ej: 28001"
                                />
                            </div>
                        </div>
                    </fieldset>

                    {/* Personas de contacto */}
                    {!isBranchFlow && (
                    <fieldset className="border rounded p-3">
                        <legend className="text-xs font-semibold text-gray-500 uppercase px-1">Personas de contacto</legend>
                        {/* B3 — antes esto estaba congelado tras el alta ("Los contactos
                            no pueden modificarse tras la creación"). Ahora se pueden
                            añadir y quitar: el backend invita a los nuevos y revoca el
                            acceso de los que desaparecen. */}
                        <p className="text-xs text-gray-400 mt-1 mb-3">
                            Son los administradores del centro. Al añadir uno recibe una invitación por
                            email; al quitarlo se le revoca el acceso. Entre 1 y 3.
                        </p>
                        <div className="space-y-3">
                            {contactFields.map((field, index) => (
                                <div key={field.id} className="grid grid-cols-3 gap-2 items-start">
                                    <div>
                                        <input
                                            type="text"
                                            {...register(`contactPersons.${index}.name`, { required: 'Requerido' })}
                                            className="w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:bg-gray-50 disabled:text-gray-500"
                                            placeholder="Nombre"
                                        />
                                        {errors.contactPersons?.[index]?.name && (
                                            <p className="text-red-500 text-xs mt-1">{errors.contactPersons[index].name?.message}</p>
                                        )}
                                    </div>
                                    <div>
                                        <input
                                            type="email"
                                            {...register(`contactPersons.${index}.email`, {
                                                required: 'Requerido',
                                                pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Email inválido' },
                                            })}
                                            className="w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:bg-gray-50 disabled:text-gray-500"
                                            placeholder="Email"
                                        />
                                        {errors.contactPersons?.[index]?.email && (
                                            <p className="text-red-500 text-xs mt-1">{errors.contactPersons[index].email?.message}</p>
                                        )}
                                    </div>
                                    <div className="flex gap-2 items-start">
                                        <input
                                            type="text"
                                            {...register(`contactPersons.${index}.phone`)}
                                            className="w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:bg-gray-50 disabled:text-gray-500"
                                            placeholder="Teléfono (opcional)"
                                        />
                                        {/* Reenviar solo tiene sentido sobre un contacto YA guardado:
                                            uno recién escrito todavía no tiene invitación que reenviar. */}
                                        {isEditing && isSavedContact(index) && (
                                            <button
                                                type="button"
                                                onClick={() => handleResend(index)}
                                                disabled={resendingIndex !== null}
                                                title="Reenviar invitación"
                                                className="text-blue-400 hover:text-blue-600 flex-shrink-0 disabled:opacity-40 disabled:cursor-not-allowed p-2"
                                            >
                                                {resendingIndex === index
                                                    ? <Loader2 size={16} className="animate-spin" />
                                                    : <Send size={16} />}
                                            </button>
                                        )}
                                        {contactFields.length > 1 && (
                                            <button
                                                type="button"
                                                onClick={() => removeContact(index)}
                                                title="Quitar contacto"
                                                className="text-red-400 hover:text-red-600 flex-shrink-0 p-2"
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                        {contactFields.length < 3 && (
                            <button
                                type="button"
                                onClick={() => appendContact({ name: '', email: '', phone: '' })}
                                className="mt-3 flex items-center gap-1 text-sm text-blue-500 hover:text-blue-700"
                            >
                                <Plus size={14} /> Añadir contacto
                            </button>
                        )}
                    </fieldset>
                    )}

                    {/* Salas */}
                    <fieldset className="border rounded p-3">
                        <legend className="text-xs font-semibold text-gray-500 uppercase px-1">Salas</legend>
                        <p className="text-xs text-gray-400 mt-1 mb-3">Define las salas disponibles para este tenant.</p>
                        <div className="space-y-2">
                            {roomFields.map((field, index) => (
                                <div key={field.id} className="grid grid-cols-[1fr_100px_auto] gap-2 items-start">
                                    <div>
                                        <input
                                            type="text"
                                            {...register(`rooms.${index}.name`, { required: 'Nombre requerido' })}
                                            className="w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                                            placeholder="Nombre de la sala"
                                        />
                                        {errors.rooms?.[index]?.name && (
                                            <p className="text-red-500 text-xs mt-1">{errors.rooms[index].name?.message}</p>
                                        )}
                                    </div>
                                    <div>
                                        <input
                                            type="number"
                                            min={1}
                                            {...register(`rooms.${index}.capacity`, {
                                                required: 'Requerido',
                                                min: { value: 1, message: 'Mínimo 1' },
                                            })}
                                            className="w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                                            placeholder="Capacidad"
                                        />
                                        {errors.rooms?.[index]?.capacity && (
                                            <p className="text-red-500 text-xs mt-1">{errors.rooms[index].capacity?.message}</p>
                                        )}
                                    </div>
                                    {roomFields.length > 1 && (
                                        <button
                                            type="button"
                                            onClick={() => removeRoom(index)}
                                            className="text-red-400 hover:text-red-600 mt-2"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                        <button
                            type="button"
                            onClick={() => appendRoom({ name: '', capacity: 10 })}
                            className="mt-3 flex items-center gap-1 text-sm text-blue-500 hover:text-blue-700"
                        >
                            <Plus size={14} /> Añadir sala
                        </button>
                    </fieldset>

                    {/* Límite de usuarios administradores (M4) — solo tenant raíz */}
                    {!isBranchFlow && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Límite de usuarios administradores
                            </label>
                            <input
                                type="number"
                                min={1}
                                {...register('limits.maxUsers', {
                                    valueAsNumber: true,
                                    min: { value: 1, message: 'Debe ser al menos 1' },
                                })}
                                className="w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                                placeholder="5"
                            />
                            <p className="text-xs text-gray-400 mt-1">
                                Máximo de administradores en este centro y sus sucursales. Por defecto 5.
                            </p>
                            {errors.limits?.maxUsers && (
                                <p className="text-red-500 text-xs mt-1">{errors.limits.maxUsers.message}</p>
                            )}
                        </div>
                    )}

                    {/* Logo */}
                    <ImageUpload
                        label="Logo"
                        value={logo}
                        onChange={(base64) => setValue('metadata.logo', base64)}
                    />

                    {/* Estado (solo al editar) */}
                    {isEditing && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
                            <select
                                {...register('status')}
                                className="w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                            >
                                <option value="active">Activo</option>
                                <option value="inactive">Inactivo</option>
                                <option value="suspended">Suspendido</option>
                            </select>
                        </div>
                    )}

                    {/* Metadata */}
                    <fieldset className="border rounded p-3">
                        <legend className="text-xs font-semibold text-gray-500 uppercase px-1">Configuración</legend>

                        {/* Colores */}
                        <div className="grid grid-cols-2 gap-3 mt-3">
                            <ColorPicker
                                label="Color primario"
                                value={primaryColor}
                                onChange={(v) => setValue('metadata.colors.primary', v)}
                                placeholder="#000000"
                            />
                            <ColorPicker
                                label="Color secundario"
                                value={secondaryColor}
                                onChange={(v) => setValue('metadata.colors.secondary', v)}
                                placeholder="#ffffff"
                            />
                        </div>
                    </fieldset>

                    {/* Acciones */}
                    <div className="flex justify-end gap-3 pt-2 border-t">
                        <Button
                            type="button"
                            variant="secondary"
                            onClick={onClose}
                            disabled={isPending}
                        >
                            Cancelar
                        </Button>
                        <Button
                            type="submit"
                            disabled={isPending}
                        >
                            {isPending && <Loader2 size={14} className="animate-spin" />}
                            {isEditing ? 'Guardar cambios' : (isBranchFlow ? 'Crear Sucursal' : 'Crear Tenant')}
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default TenantFormModal;
